import { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Check, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): Crop {
  return centerCrop(
    makeAspectCrop({ unit: "%", width: 90 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight,
  );
}

async function getCroppedBlob(
  image: HTMLImageElement,
  crop: PixelCrop,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const sourceX = crop.x * scaleX;
  const sourceY = crop.y * scaleY;
  const sourceW = crop.width * scaleX;
  const sourceH = crop.height * scaleY;

  canvas.width = sourceW;
  canvas.height = sourceH;

  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, sourceX, sourceY, sourceW, sourceH, 0, 0, sourceW, sourceH);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob failed"));
      },
      "image/jpeg",
      0.92,
    );
  });
}

export function ImageCropper({
  open,
  onOpenChange,
  imageSrc,
  aspect,
  onCropComplete,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  imageSrc: string;
  aspect: number;
  onCropComplete: (blob: Blob) => void;
}) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    return () => {
      setCrop(undefined);
      setCompletedCrop(undefined);
    };
  }, [imageSrc]);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      const c = centerAspectCrop(width, height, aspect);
      setCrop(c);
      // Initialize completedCrop so confirm works without any drag
      const pixelCrop: PixelCrop = {
        unit: "px",
        x: Math.round((c.x / 100) * width),
        y: Math.round((c.y / 100) * height),
        width: Math.round((c.width / 100) * width),
        height: Math.round((c.height / 100) * height),
      };
      setCompletedCrop(pixelCrop);
    },
    [aspect],
  );

  const handleConfirm = async () => {
    if (!imgRef.current || !completedCrop) {
      toast({
        variant: "destructive",
        title: "Recadrage impossible",
        description: "Aucune zone de recadrage sélectionnée. Réessayez.",
      });
      return;
    }
    if (completedCrop.width < 4 || completedCrop.height < 4) {
      toast({
        variant: "destructive",
        title: "Zone de recadrage trop petite",
        description: "Agrandissez la zone de recadrage puis réessayez.",
      });
      return;
    }
    setIsProcessing(true);
    try {
      const blob = await getCroppedBlob(imgRef.current, completedCrop);
      onCropComplete(blob);
      onOpenChange(false);
    } catch (err) {
      console.error("Crop error:", err);
      toast({
        variant: "destructive",
        title: "Erreur lors du recadrage",
        description: "Impossible de traiter cette image. Essayez une autre photo.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const aspectLabel = aspect === 1 ? "1:1" : "16:9";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Recadrer la photo</DialogTitle>
          <DialogDescription>
            Déplacez et redimensionnez le cadre pour ajuster le recadrage ({aspectLabel})
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          <div className="w-full overflow-hidden rounded-lg border bg-black/5 flex items-center justify-center"
            style={{ maxHeight: 400 }}>
            <ReactCrop
              crop={crop}
              onChange={(pixelCrop, percentCrop) => {
                setCrop(percentCrop);
                setCompletedCrop(pixelCrop);
              }}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Image à recadrer"
                style={{ maxHeight: 400, width: "auto" }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          </div>
          <p className="text-xs text-muted-foreground">
            Glissez les bords du cadre pour zoomer / dézoomer sur la zone souhaitée
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing} data-testid="button-cancel-crop">
            <X className="w-4 h-4 mr-1" />Annuler
          </Button>
          <Button onClick={handleConfirm} disabled={isProcessing} data-testid="button-confirm-crop">
            <Check className="w-4 h-4 mr-1" />{isProcessing ? "Traitement..." : "Valider le recadrage"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
